import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  getReferralCaselist,
  getPatientStageTimeline,
} from '@/services/referringDirectoryService';
import {
  treatmentStatusMeta,
  stageLabel,
  STAGE_LABEL,
  formatDay,
} from '@/lib/treatmentStatus';
import PatientVisitHistory from '@/components/PatientVisitHistory';

const PAGE_SIZE = 50;

const StageTimeline: React.FC<{ platoPatientId: string }> = ({ platoPatientId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['patient-stage-timeline', platoPatientId],
    queryFn: () => getPatientStageTimeline(platoPatientId),
  });
  const rows = (data ?? []).filter((r) => r.stage !== 'other');
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading stages…
      </div>
    );
  }
  if (rows.length === 0) {
    return <p className="py-1 text-xs text-muted-foreground">No treatment stages captured yet.</p>;
  }
  return (
    <div className="mb-3 space-y-1">
      <p className="text-xs font-medium text-slate-600">Treatment stages</p>
      {rows.map((r, i) => (
        <div key={`${r.stage_date}-${i}`} className="flex items-baseline gap-2 text-xs">
          <span className="w-20 shrink-0 tabular-nums text-slate-500">{formatDay(r.stage_date)}</span>
          <span className="font-medium text-slate-800">{STAGE_LABEL[r.stage] ?? r.stage}</span>
          <span className="truncate text-slate-400">{r.description}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Global referral case list: every patient referred to Twin City (from the Plato
 * "Referred By" mapping) in one flat, filterable/sortable table, with the coarse
 * treatment status + latest endodontic stage. Click a row to expand the stage
 * timeline + Plato visit history.
 */
const ReferralCaselist: React.FC = () => {
  const [search, setSearch] = useState('');
  const [clinicFilter, setClinicFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'status' | 'recent'>('name');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const caselistQuery = useQuery({
    queryKey: ['referral-caselist'],
    queryFn: getReferralCaselist,
  });

  const allRows = caselistQuery.data ?? [];

  const clinics = useMemo(() => {
    const set = new Set<string>();
    allRows.forEach((r) => r.clinic_name && set.add(r.clinic_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allRows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = allRows.filter((r) => {
      const matchesSearch =
        term === '' ||
        (r.patient_name ?? '').toLowerCase().includes(term) ||
        (r.clinic_name ?? '').toLowerCase().includes(term) ||
        (r.dentist_name ?? '').toLowerCase().includes(term);
      const matchesClinic = clinicFilter === 'all' || r.clinic_name === clinicFilter;
      const matchesStage = stageFilter === 'all' || (r.current_stage ?? '') === stageFilter;
      const matchesStatus =
        statusFilter === 'all' || (r.treatment_status ?? 'ongoing') === statusFilter;
      return matchesSearch && matchesClinic && matchesStage && matchesStatus;
    });
    const statusRank: Record<string, number> = { ongoing: 0, discharge_drafted: 1, discharged: 2 };
    rows.sort((a, b) => {
      if (sortBy === 'status') {
        return (
          (statusRank[a.treatment_status ?? 'ongoing'] ?? 0) -
            (statusRank[b.treatment_status ?? 'ongoing'] ?? 0) ||
          (a.patient_name ?? '').localeCompare(b.patient_name ?? '')
        );
      }
      if (sortBy === 'recent') {
        const av = a.last_seen ?? '';
        const bv = b.last_seen ?? '';
        if (av === bv) return (a.patient_name ?? '').localeCompare(b.patient_name ?? '');
        return bv.localeCompare(av);
      }
      return (a.patient_name ?? '').localeCompare(b.patient_name ?? '');
    });
    return rows;
  }, [allRows, search, clinicFilter, stageFilter, statusFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetPage = () => setPage(1);

  return (
    <Card>
      <CardHeader className="gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">
            Referral case list
            {!caselistQuery.isLoading && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {filtered.length} of {allRows.length} patients
              </span>
            )}
          </CardTitle>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <Input
            placeholder="Search patient / clinic / dentist"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
            className="col-span-2 sm:col-span-1"
          />
          <Select value={clinicFilter} onValueChange={(v) => { setClinicFilter(v); resetPage(); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clinics</SelectItem>
              {clinics.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); resetPage(); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any stage</SelectItem>
              <SelectItem value="consult">Consult</SelectItem>
              <SelectItem value="stage_1">Stage 1</SelectItem>
              <SelectItem value="stage_2">Stage 2</SelectItem>
              <SelectItem value="review">Review</SelectItem>
              <SelectItem value="6_month_review">6-month review</SelectItem>
              <SelectItem value="discharge">Discharge</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); resetPage(); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="ongoing">Treatment ongoing</SelectItem>
              <SelectItem value="discharge_drafted">Discharge drafted</SelectItem>
              <SelectItem value="discharged">Discharged</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(v: 'name' | 'status' | 'recent') => setSortBy(v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: Name</SelectItem>
              <SelectItem value="status">Sort: Status</SelectItem>
              <SelectItem value="recent">Sort: Recently seen</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="px-2 sm:px-6">
        {caselistQuery.isLoading ? (
          <div className="py-16 text-center text-sm text-slate-500">Loading referral case list…</div>
        ) : caselistQuery.isError ? (
          <div className="py-16 text-center text-sm text-rose-700">Could not load the case list.</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">No patients match these filters.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Patient</TableHead>
                    <TableHead>Clinic</TableHead>
                    <TableHead>Referring dentist</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map((r) => {
                    const isOpen = expandedId === r.plato_patient_id;
                    const treat = treatmentStatusMeta(r.treatment_status);
                    const stage = stageLabel(r.current_stage);
                    return (
                      <Fragment key={r.plato_patient_id}>
                        <TableRow
                          className="cursor-pointer"
                          onClick={() =>
                            setExpandedId(isOpen ? null : r.plato_patient_id)
                          }
                        >
                          <TableCell className="text-slate-400">
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">
                            {r.patient_name ?? 'Unknown'}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {r.clinic_name ?? '—'}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {r.dentist_name && r.dentist_name !== '[Blank]' ? r.dentist_name : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-slate-700">{stage ?? '—'}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={cn('text-[11px] font-normal', treat.className)}
                            >
                              {treat.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm tabular-nums text-slate-500">
                            {formatDay(r.last_seen) || '—'}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-slate-50/60">
                              <StageTimeline platoPatientId={r.plato_patient_id} />
                              <PatientVisitHistory platoReferenceId={r.plato_patient_id} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-3 text-sm text-slate-500">
                <span>
                  Page {safePage} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralCaselist;
